import { useCallback, useEffect, useState } from 'react';
import { ShipmentTracking } from '@/components/logistics/ShipmentTracking';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { formatCOP, formatDate, statusLabel, statusColor } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';
import { Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { SELLER } from '@/constants/testIds';
import { InvoiceSection } from '@/components/InvoiceSection';

const STATUSES = [
  { v: 'all', l: 'Todos' }, { v: 'paid', l: 'Pagado' },
  { v: 'shipped', l: 'Despachado' }, { v: 'delivered', l: 'Entregado' },
  { v: 'cancelled', l: 'Cancelado' },
];

export default function SellerOrders() {
  const [orders, setOrders] = useState([]);
  const [tracking, setTracking] = useState({});
  const [carriers, setCarriers] = useState({});
  const [filters, setFilters] = useState({ q: '', status: 'all', date_from: '', date_to: '' });

  useSEO({
    title: 'Pedidos y despachos | Panel del vendedor',
    description: 'Gestiona los pedidos recibidos, actualiza tracking y estados de despacho.',
    canonical: '/vendedor/pedidos',
  });

  const fetchData = useCallback(async () => {
    const params = {};
    if (filters.q.trim()) params.q = filters.q.trim();
    if (filters.status !== 'all') params.status = filters.status;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    const { data } = await api.get('/sellers/me/orders', { params });
    setOrders(data.items || []);
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const update = async (order_id, status) => {
    try {
      await api.put(`/orders/${order_id}/status`, {
        status, tracking_code: tracking[order_id] || undefined, carrier: carriers[order_id] || undefined,
      });
      toast.success(`Pedido: ${statusLabel[status]}`);
      fetchData();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Error'));
    }
  };

  const clearFilters = () => setFilters({ q: '', status: 'all', date_from: '', date_to: '' });
  const hasFilters = filters.q || filters.status !== 'all' || filters.date_from || filters.date_to;

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Vendedor</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Pedidos y despachos</h1>

        {/* Filters */}
        <div className="mt-6 border border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-[1fr,160px,160px,160px,auto] gap-3 items-end">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Buscar</label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                data-testid="seller-orders-search"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="ID pedido, comprador, ciudad, tracking..."
                className="pl-9 rounded-none"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Estado</label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger data-testid="seller-orders-status" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Desde</label>
            <Input data-testid="seller-orders-date-from" type="date" value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="rounded-none mt-1" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Hasta</label>
            <Input data-testid="seller-orders-date-to" type="date" value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="rounded-none mt-1" />
          </div>
          {hasFilters && (
            <Button variant="outline" onClick={clearFilters} className="rounded-none h-10">
              <X className="h-4 w-4 mr-1" /> Limpiar
            </Button>
          )}
        </div>

        <div className="mt-3 text-sm text-slate-600">{orders.length} pedidos</div>

        <div className="mt-3 space-y-4">
          {orders.length === 0 && <div className="border border-slate-200 p-12 text-center text-slate-500 bg-slate-50">Sin resultados con estos filtros</div>}
          {orders.map((o) => (
            <div key={o.order_id} className="border border-slate-200 bg-white">
              <div className="flex items-center justify-between flex-wrap gap-2 px-5 py-3 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center gap-4 text-sm">
                  <span className="font-mono text-slate-500">{o.order_id}</span>
                  <span className="text-slate-700">{o.buyer_name}</span>
                  <span className="text-slate-500">{formatDate(o.created_at)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={`rounded-none border ${statusColor[o.status]}`}>{statusLabel[o.status] || o.status}</Badge>
                  <span className="font-display font-bold">{formatCOP(o.total)}</span>
                </div>
              </div>
              <div className="p-4 text-sm">
                <div className="text-slate-700">
                  <strong>Envío:</strong> {o.shipping_address}, {o.shipping_city}{o.shipping_department ? ` (${o.shipping_department})` : ''} · {o.shipping_phone}
                </div>
                {['configured', 'automarket'].includes(o.shipping?.mode) && (
                  <div className="mt-1 text-xs text-slate-600" data-testid={`seller-order-shipping-${o.order_id}`}>
                    Productos {formatCOP(o.subtotal)} · Envío {o.shipping_amount ? formatCOP(o.shipping_amount) : 'gratis'} · {o.shipping.carrier} · {o.shipping.days_min}–{o.shipping.days_max} días
                  </div>
                )}
                <div className="mt-2 text-slate-700">
                  {o.items.map((it) => `${it.qty}× ${it.name}`).join(' · ')}
                </div>
                {o.tracking_code && (
                  <div className="mt-2 text-xs"><span className="font-bold uppercase tracking-[0.2em] text-slate-500 mr-1">Tracking</span><span className="font-mono">{o.carrier ? `${o.carrier} · ` : ''}{o.tracking_code}</span></div>
                )}
                {o.logistics_mode === 'automarket' ? (
                  <div className="mt-3 border border-sky-200 bg-sky-50 p-2 text-xs text-sky-900" data-testid={`seller-order-managed-${o.order_id}`}>
                    AutoMarket recoge y entrega este pedido. Ten el paquete listo en tu dirección de recogida; la guía y los estados se actualizan automáticamente.
                    {o.shipping?.breakdown?.logistics_fee ? ` Cargo logístico: ${formatCOP(o.shipping.breakdown.logistics_fee)} (se descuenta de tu desembolso).` : ''}
                    <ShipmentTracking orderId={o.order_id} />
                  </div>
                ) : (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <Input
                    data-testid={`seller-order-carrier-${o.order_id}`}
                    placeholder={o.carrier || o.shipping?.carrier || 'Transportadora'}
                    value={carriers[o.order_id] || ''}
                    onChange={(e) => setCarriers({ ...carriers, [o.order_id]: e.target.value })}
                    className="rounded-none max-w-[180px] h-9"
                  />
                  <Input
                    data-testid={`seller-order-tracking-${o.order_id}`}
                    placeholder="Número de guía"
                    value={tracking[o.order_id] || ''}
                    onChange={(e) => setTracking({ ...tracking, [o.order_id]: e.target.value })}
                    className="rounded-none max-w-xs h-9"
                  />
                  {o.status === 'paid' && (
                    <Button data-testid={SELLER.shipBtn(o.order_id)} onClick={() => update(o.order_id, 'shipped')}
                      className="rounded-none bg-indigo-700 hover:bg-indigo-800 h-9">Despachado</Button>
                  )}
                  {o.status === 'shipped' && (
                    <Button data-testid={SELLER.deliverBtn(o.order_id)} onClick={() => update(o.order_id, 'delivered')}
                      className="rounded-none bg-emerald-700 hover:bg-emerald-800 h-9">Entregado</Button>
                  )}
                </div>
                )}
              </div>
              <InvoiceSection orderId={o.order_id} role="seller" />
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
